import type { MaintenanceCacheInvalidationReason } from '../services/api'

export type CacheDebugLogLevel = 'debug' | 'info' | 'warn' | 'error'

export interface CacheDebugLogEntry {
  id: string
  level: CacheDebugLogLevel
  source: string
  message: string
  timestamp: number
  data?: unknown
}

const STORAGE_KEY = 'maintenanceCacheDebugLogs'
const MAX_ENTRIES = 200
const ENTRY_TTL_MS = 10 * 60 * 1000 // 10 minutos
const LOG_EVENT_NAME = 'maintenance-cache-debug-log' as const

const isBrowserEnvironment = () =>
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'

const canDispatchEvents = () =>
  isBrowserEnvironment() &&
  typeof window.dispatchEvent === 'function' &&
  typeof window.CustomEvent === 'function'

export type CacheDebugLogEventDetail = {
  entry: CacheDebugLogEntry | null
  entries: CacheDebugLogEntry[]
}

const dispatchLogEvent = (entries: CacheDebugLogEntry[], entry: CacheDebugLogEntry | null) => {
  if (!canDispatchEvents()) {
    return
  }

  try {
    window.dispatchEvent(
      new CustomEvent<CacheDebugLogEventDetail>(LOG_EVENT_NAME, {
        detail: {
          entry,
          entries
        }
      })
    )
  } catch (error) {
    // Ignora falhas silenciosamente para não quebrar o fluxo de logs
    console.warn('[cacheDebugLogger] Falha ao despachar evento de log', error)
  }
}

const safeParseStoredEntries = (): CacheDebugLogEntry[] => {
  if (!isBrowserEnvironment()) {
    return []
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return []
    }

    const parsed = JSON.parse(raw) as CacheDebugLogEntry[]
    if (!Array.isArray(parsed)) {
      return []
    }

    const now = Date.now()

    return parsed
      .filter(entry => typeof entry?.timestamp === 'number')
      .filter(entry => now - entry.timestamp <= ENTRY_TTL_MS)
  } catch (error) {
    console.warn('[cacheDebugLogger] Falha ao ler localStorage', error)
    return []
  }
}

const persistEntries = (entries: CacheDebugLogEntry[]): CacheDebugLogEntry[] => {
  if (!isBrowserEnvironment()) {
    return entries
  }

  try {
    const entriesToStore = entries.slice(-MAX_ENTRIES)
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entriesToStore))
    return entriesToStore
  } catch (error) {
    console.warn('[cacheDebugLogger] Falha ao gravar localStorage', error)
    return entries
  }
}

const buildLogEntryId = () => `cache-log-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

const pushEntryInternal = (entry: CacheDebugLogEntry): CacheDebugLogEntry => {
  const existingEntries = safeParseStoredEntries()
  const updatedEntries = [...existingEntries, entry]
  const persistedEntries = persistEntries(updatedEntries)
  dispatchLogEvent(persistedEntries, entry)
  return entry
}

const makeEntry = (
  level: CacheDebugLogLevel,
  source: string,
  message: string,
  data?: unknown
): CacheDebugLogEntry => ({
  id: buildLogEntryId(),
  level,
  source,
  message,
  timestamp: Date.now(),
  data
})

export const cacheDebugLogger = {
  push: (level: CacheDebugLogLevel, source: string, message: string, data?: unknown) =>
    pushEntryInternal(makeEntry(level, source, message, data)),

  debug: (source: string, message: string, data?: unknown) =>
    pushEntryInternal(makeEntry('debug', source, message, data)),

  info: (source: string, message: string, data?: unknown) =>
    pushEntryInternal(makeEntry('info', source, message, data)),

  warn: (source: string, message: string, data?: unknown) =>
    pushEntryInternal(makeEntry('warn', source, message, data)),

  error: (source: string, message: string, data?: unknown) =>
    pushEntryInternal(makeEntry('error', source, message, data)),

  list: (): CacheDebugLogEntry[] => safeParseStoredEntries(),

  consume: (): CacheDebugLogEntry[] => {
    const entries = safeParseStoredEntries()
    if (isBrowserEnvironment()) {
      window.localStorage.removeItem(STORAGE_KEY)
      dispatchLogEvent([], null)
    }
    return entries
  },

  clear: () => {
    if (!isBrowserEnvironment()) {
      return
    }
    window.localStorage.removeItem(STORAGE_KEY)
    dispatchLogEvent([], null)
  },

  appendCacheInvalidation: (
    source: string,
    reason: MaintenanceCacheInvalidationReason,
    payload: {
      requestId?: string
      storeId?: string
      extra?: Record<string, unknown>
    } = {}
  ) => {
    const { requestId, storeId, extra } = payload

    const entry = cacheDebugLogger.debug(source, `Cache invalidation: ${reason}`, {
      reason,
      requestId,
      storeId,
      ...extra
    })

    // Garante que ouvintes recebam atualização do buffer mesmo sem navegação
    const entries = cacheDebugLogger.list()
    dispatchLogEvent(entries, entry)
  }
}

export const CACHE_DEBUG_LOG_EVENT = LOG_EVENT_NAME

export type CacheDebugLogger = typeof cacheDebugLogger
